import { useRestApiProvider } from "@jugl-web/rest-api";
import { InteractiveContainer, TextAreaInput, Tooltip } from "@jugl-web/ui-kit";
import { cx, useFormFieldsValidators, useTranslations } from "@jugl-web/utils";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import React, { ReactNode } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { CRMInfoSection } from "../../../../components/CRMInfoSection";
import { VoiceGeneralFormValues } from "../../types";
import { ReactComponent as InfoIcon } from "./assets/info.svg";

const tooltipRichTextValues = {
  b: (chunks: ReactNode) => <span className="font-semibold">{chunks}</span>,
  br: <br />,
};

export const AIInstructionsSection: React.FC = () => {
  const { t } = useTranslations();
  const { requiredValidator } = useFormFieldsValidators();
  const { entityId } = useEntitySelectedProvider();
  const { integrationsApi } = useRestApiProvider();
  const { data: agentConfig } = integrationsApi.useGetAiAgentConfigQuery({
    entityId,
  });
  const {
    control,
    formState: { errors },
    clearErrors,
  } = useFormContext<VoiceGeneralFormValues>();
  const useBaseInstructions = useWatch({
    control,
    name: "use_base_instructions",
  });
  const baseInstructions = agentConfig?.config.instructions ?? "";

  return (
    <CRMInfoSection
      title={t({
        id: "client-support-page.ai-instructions-for-voice",
        defaultMessage: "AI Instructions for Phone Calls",
      })}
      subtitle={t({
        id: "client-support-page.ai-instructions-for-voice-description",
        defaultMessage: "Use Base Instructions or indicate custom one.",
      })}
      rightContent={
        <Controller
          control={control}
          name="use_base_instructions"
          render={({ field: { value, onChange } }) => (
            <div className="flex h-10.25 shrink-0 items-center gap-1.5 rounded-full border border-solid border-[#F2F2F2] bg-white p-1 max-lg:h-9">
              <Tooltip
                placement="top"
                renderTrigger={({ props, ref }) => (
                  <InteractiveContainer
                    ref={ref}
                    {...props}
                    className={cx(
                      "flex h-full items-center gap-1.5 rounded-full px-3.5 font-secondary text-sm whitespace-nowrap transition-colors max-lg:px-2.5",
                      value
                        ? "bg-linear-to-r from-[#E5EFFF] to-[#E9EBFF] text-black"
                        : "bg-transparent text-dark-600"
                    )}
                    onClick={() => {
                      onChange(true);
                      clearErrors("instructions");
                    }}
                  >
                    <InfoIcon className="shrink-0" />
                    {t({
                      id: "client-support-page.base",
                      defaultMessage: "Base",
                    })}
                  </InteractiveContainer>
                )}
                className="flex max-w-[320px] flex-col gap-4"
              >
                <span>
                  {t(
                    {
                      id: "client-support-page.voice-ai-tooltip-instructions",
                      defaultMessage:
                        "<b>🔵 Instructions:</b>{br}You can set custom AI Agent instructions or use available base one",
                    },
                    tooltipRichTextValues
                  )}
                </span>
                <span>
                  {t(
                    {
                      id: "client-support-page.voice-ai-tooltip-knowledge",
                      defaultMessage:
                        "<b>🟢 Knowledge:</b>{br}AI Agent will use available Knowledge, Business Name and Training Sources",
                    },
                    tooltipRichTextValues
                  )}
                </span>
                <span>
                  {t(
                    {
                      id: "client-support-page.voice-ai-tooltip-not-supported",
                      defaultMessage:
                        "<b>❌ Not supported:</b>{br}Automations, Collect Leads, Escalate to Human, and Agent tone.",
                    },
                    tooltipRichTextValues
                  )}
                </span>
              </Tooltip>
              <InteractiveContainer
                className={cx(
                  "flex h-full items-center rounded-full px-3.5 font-secondary text-sm whitespace-nowrap transition-colors max-lg:px-2.5",
                  !value
                    ? "bg-linear-to-r from-[#E5EFFF] to-[#E9EBFF] text-black"
                    : "bg-transparent text-dark-600"
                )}
                onClick={() => onChange(false)}
              >
                {t({
                  id: "client-support-page.custom",
                  defaultMessage: "Custom",
                })}
              </InteractiveContainer>
            </div>
          )}
        />
      }
    >
      <div className="h-px w-full shrink-0 bg-[#F2F2F2]" />
      <Controller
        control={control}
        name="instructions"
        rules={{
          validate: (value, formValues) => {
            if (formValues.use_base_instructions) {
              return true;
            }

            const { validate } = requiredValidator();

            if (typeof validate === "function") {
              return validate(value, formValues);
            }

            return true;
          },
        }}
        render={({ field: { value, onChange, ref } }) => (
          <TextAreaInput
            classNames={{
              wrapperClassName: "w-full",
              inputClassName:
                "min-h-[120px] max-h-[320px] overflow-y-auto jugl__custom-scrollbar",
            }}
            ref={ref}
            value={useBaseInstructions ? baseInstructions : value}
            onChange={(event) => onChange(event.target.value.trimStart())}
            isDisabled={useBaseInstructions}
            isInvalid={!!errors.instructions}
            errorMessage={errors.instructions?.message}
            placeholder={t({
              id: "client-support-page.enter-instructions-voice",
              defaultMessage: "Enter instructions for AI Agent Phone calls",
            })}
          />
        )}
      />
    </CRMInfoSection>
  );
};
